// shared/geo – address suggestions on existing fields (contract X, owner RimoChantiers). Every address field of the
// suite comes from Claude Design, so this module never draws a field of its own: the program marks its fields with
// `data-addr` (compiler patch) and mounts <AddressSuggest/> once.
//
//   data-addr="street"            the field that searches (on the <input> itself or on a wrapper around it)
//   data-addr="zip|city|country"  the fields a picked suggestion fills (optional – see the event below)
//   data-addr-group               the element that holds one address (dialog part, card); without one: the nearest
//                                 form / [role=dialog] / the document
//   data-addr-mode="click"        private addresses (RimoCompta Personnel): no search while typing, only on the button
//   data-addr-key="…"             free key, passed on in the event (e.g. which address of a dialog)
//
// Picking a suggestion writes every value through the field's native value setter and fires `input` (bubbles), so
// React state, auto-save and drafts see it; then `rc-address` is dispatched on the street field's element with
// `{ street, zip, city, country, lat, lng, label, key }` (for the coordinates). Foreign addresses: nothing is written
// unless the person picks a suggestion. Touch: rows ≥ 44 px, pick on pointerup, hover only with a fine pointer, the list
// stays above the iOS keyboard (visualViewport).
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import './geo.css';

export interface Suggestion { street: string; zip: string; city: string; country?: string; lat: number; lng: number; label: string }
export type GeoLang = 'de' | 'fr' | 'sh' | 'pt';
export interface AddressSuggestProps {
  /** the program's own server: GET /api/v1/geo/search?q= (session cookie or token – the program decides) */
  search: (q: string) => Promise<Suggestion[]>;
  /** the person's language; a function is read at every render (the language often arrives after mounting) */
  lang?: GeoLang | string | (() => string);
  /** default 250 ms after the last key */
  debounceMs?: number;
}

const TEXT: Record<GeoLang, { find: string; none: string }> = {
  de: { find: 'Adresse suchen', none: 'Keine Adresse gefunden' },
  fr: { find: 'Rechercher l’adresse', none: 'Aucune adresse trouvée' },
  sh: { find: 'Traži adresu', none: 'Adresa nije pronađena' },
  pt: { find: 'Procurar morada', none: 'Nenhuma morada encontrada' },
};
export const geoText = (lang?: string | (() => string)) => { const l = typeof lang === 'function' ? lang() : lang; return TEXT[(l && l in TEXT ? l : 'de') as GeoLang]; };

type Role = 'street' | 'zip' | 'city' | 'country';
const ROLES: Role[] = ['street', 'zip', 'city', 'country'];

/** The role of an input: data-addr on the input or on its nearest marked wrapper. */
export function roleOf(input: Element): { role: Role; el: HTMLElement } | null {
  const el = input.closest('[data-addr]') as HTMLElement | null;
  const role = el?.dataset.addr as Role | undefined;
  return el && role && ROLES.includes(role) ? { role, el } : null;
}

const groupOf = (el: HTMLElement): ParentNode => el.closest('[data-addr-group]') ?? el.closest('form') ?? el.closest('[role="dialog"]') ?? document;

/** The input (or select / textarea) of a marked element. */
const fieldIn = (el: Element): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null =>
  el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement ? el : el.querySelector('input, select, textarea');

/** Writes a value the way a person would: native setter + input/change events (React, auto-save and drafts see it). */
export function writeValue(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, value: string) {
  if (field instanceof HTMLSelectElement) {
    const opt = Array.from(field.options).find((o) => o.value === value || o.value.toUpperCase() === 'LU' && value === 'Luxembourg' || o.text.trim().toLowerCase() === value.toLowerCase());
    if (!opt) return;
    value = opt.value;
  }
  const proto = Object.getPrototypeOf(field) as object;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(field, value); else field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

/** The `data-addr-key` of a street field – on the field itself or on a wrapper around it (RimoRapports, 26.10.2026). */
export const addrKeyOf = (el: Pick<Element, 'closest'>): string | null =>
  (el.closest('[data-addr-key]') as HTMLElement | null)?.dataset.addrKey ?? null;

/** Fills the group of a street field with a suggestion and announces it. */
export function fill(streetEl: HTMLElement, s: Suggestion) {
  const group = groupOf(streetEl), values: Record<Role, string> = { street: s.street, zip: s.zip, city: s.city, country: 'Luxembourg' };
  for (const role of ROLES) {
    const el = role === 'street' ? streetEl : (group.querySelector(`[data-addr="${role}"]`) as HTMLElement | null);
    const field = el && fieldIn(el);
    if (field && !(field as HTMLInputElement).readOnly && !field.disabled) writeValue(field, values[role]);
  }
  streetEl.dispatchEvent(new CustomEvent('rc-address', { bubbles: true, detail: { ...s, country: s.country ?? 'LU', key: addrKeyOf(streetEl) } }));
}

interface Open { input: HTMLInputElement; el: HTMLElement; items: Suggestion[]; active: number; empty: boolean }
interface Ask { input: HTMLInputElement; el: HTMLElement }

const coarse = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

export function AddressSuggest({ search, lang, debounceMs = 250 }: AddressSuggestProps) {
  const [open, setOpen] = useState<Open | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [, redraw] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const seq = useRef(0), writing = useRef(false);
  const cur = useRef<Open | null>(null), curAsk = useRef<Ask | null>(null), searchRef = useRef(search);
  cur.current = open; curAsk.current = ask; searchRef.current = search;
  const t = geoText(lang);

  useEffect(() => {
    const run = (input: HTMLInputElement, el: HTMLElement, q: string, showEmpty: boolean) => {
      const n = ++seq.current;
      searchRef.current(q).then((items) => {
        if (n !== seq.current || document.activeElement !== input) return;
        setOpen(items.length || showEmpty ? { input, el, items, active: 0, empty: !items.length } : null);
      }, () => setOpen(null));
    };
    const streetOf = (target: EventTarget | null) => {
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return null;
      const r = roleOf(target);
      return r && r.role === 'street' ? { input: target as HTMLInputElement, el: r.el } : null;
    };
    const onInput = (e: Event) => {
      if (writing.current) return;
      const s = streetOf(e.target);
      if (!s) return;
      clearTimeout(timer.current);
      if (s.el.dataset.addrMode === 'click') { setOpen(null); return; }
      const q = s.input.value.trim();
      if (q.length < 3) { setOpen(null); return; }
      timer.current = setTimeout(() => run(s.input, s.el, q, false), debounceMs);
    };
    const onFocus = (e: FocusEvent) => {
      const s = streetOf(e.target);
      if (!s) return;
      if (s.el.dataset.addrMode === 'click') setAsk(s);
      // phones: keep the field (and the list under it) above the keyboard
      if (coarse()) setTimeout(() => s.input.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
    };
    const onBlur = (e: FocusEvent) => {
      const a = curAsk.current;
      if (a && e.target === a.input) setTimeout(() => { if (document.activeElement !== a.input) setAsk(null); }, 200);
    };
    const pick = (o: Open, s: Suggestion) => {
      setOpen(null); setAsk(null);
      writing.current = true;
      try { fill(o.el, s); } finally { writing.current = false; }
    };
    const onKey = (e: KeyboardEvent) => {
      const o = cur.current;
      if (!o || e.target !== o.input || o.empty) { if (o && e.key === 'Escape') setOpen(null); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen({ ...o, active: (o.active + (e.key === 'ArrowDown' ? 1 : -1) + o.items.length) % o.items.length }); }
      else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); pick(o, o.items[o.active]!); }
      else if (e.key === 'Escape') { e.stopPropagation(); setOpen(null); }
    };
    // pointerdown keeps the focus in the field; the pick happens on pointerup (touch: no ghost click, no hover state)
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest?.('[data-geo-item], [data-geo-find]')) { e.preventDefault(); return; }
      const o = cur.current;
      if (o && target !== o.input && !target.closest?.('.rg-list')) setOpen(null);
    };
    const onUp = (e: PointerEvent) => {
      const target = e.target as HTMLElement, o = cur.current;
      const item = target.closest?.('[data-geo-item]') as HTMLElement | null;
      if (item && o && !o.empty) { e.preventDefault(); pick(o, o.items[Number(item.dataset.geoItem)]!); return; }
      const find = target.closest?.('[data-geo-find]'), a = curAsk.current;
      if (find && a) { e.preventDefault(); const q = a.input.value.trim(); if (q.length >= 3) run(a.input, a.el, q, true); }
    };
    const onView = () => redraw((x) => x + 1);
    document.addEventListener('input', onInput, true);
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', onBlur, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
    window.visualViewport?.addEventListener('resize', onView);
    window.visualViewport?.addEventListener('scroll', onView);
    window.addEventListener('scroll', onView, true);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', onBlur, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
      window.visualViewport?.removeEventListener('resize', onView);
      window.visualViewport?.removeEventListener('scroll', onView);
      window.removeEventListener('scroll', onView, true);
    };
  }, [debounceMs]);

  const anchor = open?.input ?? ask?.input;
  if (!anchor || !anchor.isConnected) return null;
  const r = (anchor.closest('span') ?? anchor).getBoundingClientRect();
  // the visible area: above the on-screen keyboard on phones
  const vv = window.visualViewport, top0 = vv?.offsetTop ?? 0, bottom0 = top0 + (vv?.height ?? window.innerHeight);
  const rowH = coarse() ? 52 : 44, listH = Math.min(6, open?.items.length || 1) * rowH + 10;
  const below = bottom0 - r.bottom >= Math.min(listH, 180) + 8 || r.top - top0 < bottom0 - r.bottom;
  const pos: CSSProperties = { left: r.left, width: Math.max(r.width, 280), ...(below ? { top: r.bottom + 4, maxHeight: Math.max(120, bottom0 - r.bottom - 12) } : { bottom: window.innerHeight - r.top + 4, maxHeight: Math.max(120, r.top - top0 - 12) }) };

  if (!open && ask) {
    return createPortal(
      <div className="rg-ask" style={{ left: r.left, ...(below ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }) }}>
        <button type="button" data-geo-find="">{t.find}</button>
      </div>,
      document.body,
    );
  }
  if (!open) return null;
  return createPortal(
    <div className="rg-list" role="listbox" style={{ ...pos, maxHeight: Math.min(pos.maxHeight as number, 6 * rowH + 10) }}>
      {open.empty ? <div className="rg-empty">{t.none}</div> : open.items.map((p, i) => (
        <div key={p.label} role="option" aria-selected={i === open.active} data-geo-item={i} className={i === open.active ? 'rg-item rg-active' : 'rg-item'}
          onMouseMove={() => { if (i !== open.active) setOpen({ ...open, active: i }); }}>
          <span className="rg-street">{p.street}</span>
          <span className="rg-place"><span className="rg-zip">{p.zip}</span> {p.city}</span>
        </div>
      ))}
    </div>,
    document.body,
  );
}
